import { __decorate } from 'tslib';
import { isEqual, isBefore } from 'date-fns';
import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import SgdsElement from '../../base/sgds-element.js';
import { sanitizedPreviousMonth, sanitizedNextMonth } from '../../utils/time.js';
import { watch } from '../../utils/watch.js';
import css_248z from './datepicker-header2.js';

class DatepickerHeader extends SgdsElement {
    constructor() {
        super(...arguments);
        /** @internal */
        this.selectedDate = [];
    }
    _handleFocusedTabIndexChange() {
        if (this.focusedTabIndex < 3) {
            const buttonToFocus = this.shadowRoot.querySelector(`button[tabindex="${this.focusedTabIndex}"]`);
            buttonToFocus.focus();
        }
        return;
    }
    _changeView() {
        switch (this.view) {
            case "days":
                this.view = "months";
                break;
            case "months":
                this.view = "years";
                break;
        }
        this.emit("sgds-view", { detail: this.view }); // emit event to render the correct view
    }
    renderHeader(displayDate = this.displayDate, view = this.view) {
        if (view === "months") {
            return displayDate.getFullYear();
        }
        if (view === "years") {
            const CURRENT_YEAR = new Date().getFullYear();
            const displayYear = displayDate.getFullYear();
            const remainder = (displayYear - CURRENT_YEAR) % 12;
            const yearsPosition = remainder < 0 ? 12 + remainder : remainder;
            const startLimit = displayYear - yearsPosition;
            const endLimit = displayYear - yearsPosition + 12 - 1;
            return `${startLimit} - ${endLimit}`;
        }
        return `${MONTH_LABELS[displayDate.getMonth()]} ${displayDate.getFullYear()}`;
    }
    _renderHeaderTemplate() {
        return html `${this.renderHeader()}`;
    }
    /** @internal */
    handleClickPrevious() {
        const { view, displayDate, focusedDate } = this;
        let newDisplayDate = new Date(displayDate);
        if (view === "months") {
            newDisplayDate.setFullYear(newDisplayDate.getFullYear() - 1);
        }
        else if (this.view === "years") {
            newDisplayDate.setFullYear(newDisplayDate.getFullYear() - 12);
        }
        else {
            /**
             * FocusedDate gets precedence over displayDate.
             *  This happens when the arrow keys are pressed to
             *  change focus date and user clicks the arrow buttons to
             * shift months
             */
            if (focusedDate.getDate() !== displayDate.getDate()) {
                newDisplayDate = sanitizedPreviousMonth(focusedDate);
            }
            else {
                newDisplayDate = sanitizedPreviousMonth(newDisplayDate);
            }
        }
        this.displayDate = newDisplayDate; // Update the displayDate property
        // emit event to render correct view
        this.emit("sgds-change-calendar", { detail: this.displayDate });
    }
    /** @internal */
    _handleClickNext() {
        const { view, displayDate, focusedDate } = this;
        let newDisplayDate = new Date(displayDate);
        if (view === "months") {
            newDisplayDate.setFullYear(newDisplayDate.getFullYear() + 1);
        }
        else if (this.view === "years") {
            newDisplayDate.setFullYear(newDisplayDate.getFullYear() + 12);
        }
        else {
            /** FocusedDate gets precedence over displayDate  */
            if (focusedDate.getDate() !== displayDate.getDate()) {
                newDisplayDate = sanitizedNextMonth(focusedDate);
            }
            else {
                newDisplayDate = sanitizedNextMonth(newDisplayDate);
            }
        }
        this.displayDate = newDisplayDate; // Update the displayDate property
        //emit event to render correct view
        this.emit("sgds-change-calendar", { detail: this.displayDate });
    }
    _removeCaret() {
        const displayYear = this.displayDate.getFullYear();
        const displayMonth = this.displayDate.getMonth();
        const displayMonthYear = new Date(displayYear, displayMonth);
        if (this.view === "months") {
            return displayYear <= 1900;
        }
        if (this.view === "years") {
            return displayYear < 1904;
        }
        return isEqual(displayMonthYear, new Date(0, 0, 1)) || isBefore(displayMonthYear, new Date(0, 0, 1));
    }
    _ariaLabelForNextBtn() {
        const nextBtnDate = {
            days: "Show next month",
            months: "Show next year",
            years: "Show next 12 years"
        };
        return nextBtnDate[this.view];
    }
    _ariaLabelForPrevBtn() {
        const prevBtnDate = {
            days: "Show previous month",
            months: "Show previous year",
            years: "Show previous 12 years"
        };
        return prevBtnDate[this.view];
    }
    _ariaLabelForHeaderBtn() {
        const message = {
            days: `Current view is days, click to show months in ${this.displayDate.getFullYear()}`,
            months: `Current view is months, click to show years between ${this.renderHeader(this.displayDate, "years")}`,
            years: `Current view is years`
        };
        return `${this.renderHeader()}. ${message[this.view]}`;
    }
    render() {
        return html `
      <div class="datepicker-header dropdown-header" role="heading">
        <div class="text-center d-flex justify-content-between align-items-center">
          <button
            @click="${this.handleClickPrevious}"
            tabindex="0"
            class=${classMap({ invisible: this._removeCaret() })}
            aria-label=${this._ariaLabelForPrevBtn()}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              class="bi bi-chevron-left"
              viewBox="0 0 16 16"
            >
              <path
                fill-rule="evenodd"
                d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"
              />
            </svg>
          </button>
          <button
            @click=${this._changeView}
            class=${classMap({ disabled: this.view === "years" })}
            tabindex="1"
            aria-disabled=${this.view === "years" ? "true" : "false"}
            aria-live="polite"
          >
            ${this._renderHeaderTemplate()}
          </button>
          <button @click="${this._handleClickNext}" tabindex="2" aria-label=${this._ariaLabelForNextBtn()}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              class="bi bi-chevron-right"
              viewBox="0 0 16 16"
            >
              <path
                fill-rule="evenodd"
                d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"
              />
            </svg>
          </button>
        </div>
      </div>
    `;
    }
}
DatepickerHeader.styles = [SgdsElement.styles, css_248z];
__decorate([
    property({ attribute: false })
], DatepickerHeader.prototype, "displayDate", void 0);
__decorate([
    property({ attribute: false })
], DatepickerHeader.prototype, "focusedDate", void 0);
__decorate([
    property({ attribute: false })
], DatepickerHeader.prototype, "selectedDate", void 0);
__decorate([
    property()
], DatepickerHeader.prototype, "view", void 0);
__decorate([
    property()
], DatepickerHeader.prototype, "focusedTabIndex", void 0);
__decorate([
    watch("focusedTabIndex", { waitUntilFirstUpdate: true })
], DatepickerHeader.prototype, "_handleFocusedTabIndexChange", null);
const MONTH_LABELS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
];

export { DatepickerHeader, MONTH_LABELS, DatepickerHeader as default };
//# sourceMappingURL=datepicker-header.js.map
